// frontend/src/contexts/AuthContext.jsx
import React, { createContext, useState, useContext, useEffect, useCallback, useMemo } from 'react';
import apiService from '../services/api'; // Your apiService for backend calls
import logger from '../utils/logger'; // Your logger utility

// --- Constants ---
const TOKEN_KEY = 'ZToken';
const USER_KEY = 'authUser';
const COMPONENT_NAME = 'AuthContext'; // For consistent logging

// --- Create Context ---
export const AuthContext = createContext(null);

// --- Custom Hook to use the Auth Context ---
export const useAuth = () => {
  const context = useContext(AuthContext);
  if (context === null) {
    throw new Error('useAuth must be used within an AuthProvider. Make sure your component is wrapped.');
  }
  return context;
};

// --- AuthProvider Component ---
export const AuthProvider = ({ children }) => {
  // Initialize state from localStorage, with proper validation for empty/invalid tokens
  const [token, setTokenState] = useState(() => {
    const storedToken = localStorage.getItem(TOKEN_KEY);
    // Only return token if it exists AND is not empty
    if (!storedToken || storedToken.trim() === '') {
      // Clean up if empty token exists
      localStorage.removeItem(TOKEN_KEY);
      return null;
    }
    return storedToken;
  });
  
  const [user, setUserState] = useState(() => {
    const storedUser = localStorage.getItem(USER_KEY);
    if (!storedUser || storedUser.trim() === '') {
      localStorage.removeItem(USER_KEY);
      return null;
    }
    
    try {
      const parsed = JSON.parse(storedUser);
      // Validate that parsed user has expected properties
      if (!parsed || typeof parsed !== 'object' || !parsed.id) {
        logger.warn(`[${COMPONENT_NAME}] Stored user data is invalid. Clearing.`);
        localStorage.removeItem(USER_KEY);
        return null;
      }
      return parsed;
    } catch (e) {
      logger.error(`[${COMPONENT_NAME}] Failed to parse stored user from localStorage on init:`, e);
      localStorage.removeItem(USER_KEY); // Clear corrupted data
      return null;
    }
  });
  
  const [authIsLoading, setAuthIsLoadingState] = useState(true);
  const [authError, setAuthErrorState] = useState(null);

  // Helper to consistently set/clear auth data in state and localStorage
  const setAuthData = useCallback((userData, currentToken) => {
    // --- IMPORTANT ORDER CHANGE: Set token in Axios first ---
    if (currentToken && currentToken.trim() !== '') {
      localStorage.setItem(TOKEN_KEY, currentToken);
      apiService.setAuthToken(currentToken); // Update Axios defaults IMMEDIATELY
      setTokenState(currentToken); // Then set state
      logger.debug(`[${COMPONENT_NAME}] Token set in state, localStorage, and Axios defaults.`);
    } else {
      localStorage.removeItem(TOKEN_KEY);
      apiService.clearAuthToken(); // Clear Axios defaults IMMEDIATELY
      setTokenState(null); // Then set state
      logger.debug(`[${COMPONENT_NAME}] Token cleared from state, localStorage, and Axios defaults.`);
    }

    // --- Then set user data ---
    if (userData && typeof userData === 'object' && userData.id) {
      localStorage.setItem(USER_KEY, JSON.stringify(userData));
      localStorage.setItem('userId', userData.id);
      setUserState(userData);
      logger.debug(`[${COMPONENT_NAME}] User data set in state and localStorage. User: ${userData.username}`);
    } else {
      localStorage.removeItem(USER_KEY);
      localStorage.removeItem('userId');
      setUserState(null);
      logger.debug(`[${COMPONENT_NAME}] User data cleared from state and localStorage.`);
    }
  }, []); // Dependencies: setUserState, setTokenState (React guarantees for setState)
  
  // Logout function: Clears local state and optionally calls backend logout
  const logout = useCallback(async (options = { callApi: true }) => {
    logger.info(`[${COMPONENT_NAME}] Logout called. Call API: ${options.callApi}`);
    setAuthIsLoadingState(true);
    try {
      // Only call backend logout if specified and function exists
      if (options.callApi && typeof apiService.auth?.logout === 'function') {
        await apiService.auth.logout();
        logger.debug(`[${COMPONENT_NAME}] Backend logout API call successful.`);
      }
    } catch (err) {
      // Log error but don't block client-side logout if backend call fails
      logger.error(`[${COMPONENT_NAME}] Backend logout API call failed (local logout will proceed):`, err.response?.data?.message || err.message);
    } finally {
      setAuthData(null, null); // Clear user and token
      setAuthErrorState(null);
      setAuthIsLoadingState(false);
      logger.info(`[${COMPONENT_NAME}] User logged out, local data and context state cleared.`);
    }
  }, [setAuthData, setAuthErrorState, setAuthIsLoadingState]);

  // Verifies the stored token by fetching user data from the backend
  const verifyTokenAndFetchUser = useCallback(async (currentTokenValue) => {
    if (!currentTokenValue || currentTokenValue.trim() === '') {
      logger.info(`[${COMPONENT_NAME}] verifyTokenAndFetchUser: No valid token provided. Ensuring logged out state.`);
      setAuthData(null, null);
      setAuthIsLoadingState(false);
      return null;
    }

    logger.info(`[${COMPONENT_NAME}] verifyTokenAndFetchUser: Verifying token and fetching user data...`);
    setAuthIsLoadingState(true);
    setAuthErrorState(null);

    try {
      if (typeof apiService.auth?.getCurrentAuthenticatedUser !== 'function') {
        throw new Error("apiService.auth.getCurrentAuthenticatedUser is not a function.");
      }

      const apiResponse = await apiService.auth.getCurrentAuthenticatedUser();
      
      const userData = apiResponse.data?.user; // This line is correct for your backend response
      
      if (userData && typeof userData === 'object' && Object.keys(userData).length > 0) {
        setAuthData(userData, currentTokenValue); // Use original token or newToken if available
        logger.info(`[${COMPONENT_NAME}] verifyTokenAndFetchUser: Token verified. User data fetched and set: ${userData.username}`);
        return userData;
      } else {
        logger.warn(`[${COMPONENT_NAME}] verifyTokenAndFetchUser: Token verification/user fetch returned no valid user data. Response:`, apiResponse);
        throw new Error("Token valid but no user data returned.");
      }
    } catch (error) {
      const errorMessage = error.response?.data?.message || error.message || "Token invalid or user fetch failed.";
      logger.warn(`[${COMPONENT_NAME}] verifyTokenAndFetchUser: Failed. Error: ${errorMessage}`, error.response || error);
      await logout({ callApi: false }); // Perform local logout if verification fails
      setAuthErrorState(errorMessage);
      return null;
    } finally {
      setAuthIsLoadingState(false);
    }
  }, [logout, setAuthData, setAuthErrorState, setAuthIsLoadingState]);


  // Initial load effect: Check for token and verify it
  useEffect(() => {
    logger.info(`[${COMPONENT_NAME}] useEffect: Initializing AuthProvider. Checking for stored token.`);
    setAuthIsLoadingState(true); // Set loading true at the start of component mount
    const storedToken = localStorage.getItem(TOKEN_KEY);
    
    // Validate token exists and is not empty
    if (storedToken && storedToken.trim() !== '') {
      logger.info(`[${COMPONENT_NAME}] useEffect: Stored token found. Verifying...`);
      verifyTokenAndFetchUser(storedToken);
    } else {
      logger.info(`[${COMPONENT_NAME}] useEffect: No valid token found. Setting initial unauthenticated state.`);
      // Clean up any invalid tokens
      if (storedToken) {
        localStorage.removeItem(TOKEN_KEY);
        localStorage.removeItem(USER_KEY);
        localStorage.removeItem('userId');
      }
      setAuthData(null, null); // Ensure no old data is lingering
      setAuthErrorState(null);
      setAuthIsLoadingState(false); // Finished initial check
    }
  }, [verifyTokenAndFetchUser, setAuthData, setAuthErrorState, setAuthIsLoadingState]);

  // Login function: Handles both credential-based login and post-registration flow
  const login = useCallback(async (loginPayload, isPostRegistration = false) => {
    logger.debug(`[${COMPONENT_NAME}] login CALLED. isPostRegistration: ${isPostRegistration}. Payload:`, loginPayload);
    setAuthIsLoadingState(true);
    setAuthErrorState(null);

    try {
      let fetchedUser;
      let currentToken;
      let responseMessage = 'Login successful!';

      if (isPostRegistration && loginPayload?.user && loginPayload?.token) {
        logger.info(`[${COMPONENT_NAME}] Path A: Post-registration flow.`);
        fetchedUser = loginPayload.user;
        currentToken = loginPayload.token;
        responseMessage = loginPayload.message || 'Registration successful!';

      } else if (!isPostRegistration && loginPayload?.emailOrUsername && loginPayload?.password) {
        logger.info(`[${COMPONENT_NAME}] Path B: Credentials login flow for user: ${loginPayload.emailOrUsername}`);

        if (typeof apiService.auth?.login !== 'function') {
            throw new Error("apiService.auth.login is not a function.");
        }
        
        const loginApiResponse = await apiService.auth.login({
            emailOrUsername: loginPayload.emailOrUsername,
            password: loginPayload.password
        });
        
        fetchedUser = loginApiResponse.data?.user;
        currentToken = loginApiResponse.data?.accessToken;
        responseMessage = loginApiResponse.message || responseMessage;
        
        if (!fetchedUser || !currentToken) {
          logger.error(`[${COMPONENT_NAME}] Credentials login: API response malformed (missing user or token inside 'data' property). Response:`, loginApiResponse);
          throw new Error("Login successful but server response was malformed (missing user or token).");
        }
        logger.info(`[${COMPONENT_NAME}] Credentials login successful. User: ${fetchedUser.username}`);
      } else {
        logger.error(`[${COMPONENT_NAME}] Path C: Login function called with invalid arguments for either flow. Payload:`, loginPayload);
        throw new Error('Login function called with invalid arguments.');
      }

      // Validate token before setting
      if (!currentToken || currentToken.trim() === '') {
        throw new Error('Invalid token received from server.');
      }

      setAuthData(fetchedUser, currentToken); // Set user and token in state and localStorage
      setAuthErrorState(null); // Clear any errors
      return { success: true, user: fetchedUser, message: responseMessage }; // Return success and user object
    } catch (error) {
      const extractedErrorMessage =
        error.response?.data?.message ||
        error.response?.data?.error ||
        (error.response?.data?.errors && Array.isArray(error.response.data.errors) ? error.response.data.errors[0].msg : null) ||
        error.message ||
        'An unknown authentication error occurred.';
      
      logger.error(`[${COMPONENT_NAME}] Login failed: ${extractedErrorMessage}`, error.response?.data || error);
      await logout({ callApi: false }); // Perform local logout on failure
      setAuthErrorState(extractedErrorMessage);
      throw error; // Re-throw the error to be caught by the calling component (LoginPage)
    } finally {
      setAuthIsLoadingState(false);
    }
  }, [setAuthData, logout, setAuthErrorState, setAuthIsLoadingState]);

  // Memoized value for context provider to prevent unnecessary re-renders
  // CRITICAL: Both token AND user must exist and be valid
  const isAuthenticated = useMemo(() => {
    const hasValidToken = !!token && token.trim() !== '';
    const hasValidUser = !!user && typeof user === 'object' && !!user.id;
    return hasValidToken && hasValidUser;
  }, [token, user]);

  const contextValue = useMemo(() => ({
    token,
    user,
    isAuthenticated,
    isLoading: authIsLoading,
    error: authError,
    login,
    logout,
    setUser: setUserState, // Allow external setting of user (e.g., from profile updates)
    setAuthError: setAuthErrorState // Allow external setting of auth error
  }), [
    token, user, isAuthenticated, authIsLoading, authError,
    login, logout, setUserState, setAuthErrorState
  ]);

  return (
    <AuthContext.Provider value={contextValue}>
      {children}
    </AuthContext.Provider>
  );
};
